"use client";

import { useEffect, useState } from "react";
import { AuthUI } from "./auth-ui";
import { usePathname } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { motion, AnimatePresence, type Variants } from "framer-motion";
import { I18nSwitcher } from "./i18n-switcher";
import { TelegramThemeSwitcher } from "./telegram-theme-switcher";
import { useLocale } from "@/i18n/context";

const menuVariants: Variants = {
  hidden: { opacity: 0, height: 0, transition: { duration: 0.3, ease: "easeInOut" } },
  visible: { 
    opacity: 1, 
    height: "100dvh", 
    transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } 
  },
  exit: { opacity: 0, height: 0, transition: { duration: 0.3, ease: "easeInOut" } }
};

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 }
  }
};

const itemVariants: Variants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0, transition: { type: "spring", stiffness: 120, damping: 14 } }
};

export function Navbar() {
  const pathname = usePathname();
  const { t } = useLocale();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const shouldHideNavbar = 
    (pathname.startsWith('/articles/') && pathname !== '/articles/create') ||
    pathname.startsWith('/login') ||
    pathname.startsWith('/volunteer-form');

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  if (shouldHideNavbar) return null;

  return (
    <nav className="sticky top-0 z-50 w-full backdrop-blur-xl bg-[#F5F0E8]/85 dark:bg-[#121A16]/85 border-b border-[#206A4F]/15 shadow-sm transition-all duration-300">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          
          <Link href="/" className="flex items-center gap-3 group cursor-pointer relative z-50">
            <div className="relative w-12 h-12 md:w-14 md:h-14 flex-shrink-0 drop-shadow-sm">
              <Image 
                src="/logo.png" 
                alt="Campaign Logo" 
                fill 
                className="object-contain group-hover:scale-110 transition-transform duration-300"
                sizes="(max-width: 768px) 48px, 56px"
                priority 
              />
            </div>
            <span className="text-xl md:text-2xl font-black text-[#206A4F] dark:text-emerald-400 tracking-tight group-hover:opacity-80 transition-opacity duration-200">
              {t.footer.brandName}
            </span>
          </Link>
          
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            <div className="flex items-center gap-2 lg:gap-4">
              <Link href="/articles" className="text-foreground/80 hover:text-accent dark:hover:text-emerald-400 px-3.5 py-2 rounded-xl text-sm font-bold hover:bg-[#206A4F]/10 dark:hover:bg-white/10 transition-all duration-200 hover:scale-[1.03] active:scale-[0.98]">
                {t.nav.articles}
              </Link>
              <Link href="/volunteer-form" className="text-foreground/80 hover:text-accent dark:hover:text-emerald-400 px-3.5 py-2 rounded-xl text-sm font-bold hover:bg-[#206A4F]/10 dark:hover:bg-white/10 transition-all duration-200 hover:scale-[1.03] active:scale-[0.98]">
                {t.nav.volunteerForm}
              </Link>
              <Link href="/contact-us" className="text-foreground/80 hover:text-accent dark:hover:text-emerald-400 px-3.5 py-2 rounded-xl text-sm font-bold hover:bg-[#206A4F]/10 dark:hover:bg-white/10 transition-all duration-200 hover:scale-[1.03] active:scale-[0.98]">
                {t.nav.contactUs}
              </Link>
            </div>

            <div className="flex items-center gap-4">
              <AuthUI />
              
              <div className="flex items-center gap-2 border-s border-accent/20 ps-3.5">
                <I18nSwitcher />
                <TelegramThemeSwitcher />
              </div>
            </div>
          </div>

          <button 
            className="md:hidden relative z-50 p-2.5 -mr-2 text-[#206A4F] dark:text-emerald-400 hover:bg-[#206A4F]/10 dark:hover:bg-white/10 rounded-full transition-colors focus:outline-none"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle Menu"
          >
            <motion.svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <motion.path 
                animate={isMobileMenuOpen ? { d: "M6 18L18 6" } : { d: "M4 6h16" }} 
                transition={{ duration: 0.3, ease: "easeInOut" }}
              />
              <motion.path 
                animate={isMobileMenuOpen ? { opacity: 0 } : { opacity: 1 }} 
                d="M4 12h16" 
                transition={{ duration: 0.2 }}
              />
              <motion.path 
                animate={isMobileMenuOpen ? { d: "M6 6l12 12" } : { d: "M4 18h16" }} 
                transition={{ duration: 0.3, ease: "easeInOut" }}
              />
            </motion.svg>
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div 
            variants={menuVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="md:hidden fixed top-0 left-0 w-full bg-[#F5F0E8] dark:bg-[#121A16] z-40 overflow-hidden border-b border-[#206A4F]/20 shadow-2xl"
          >
            <div className="container mx-auto px-6 pt-24 pb-32 h-full overflow-y-auto flex flex-col justify-between">
              
              <motion.div variants={containerVariants} initial="hidden" animate="visible" className="flex flex-col gap-5 text-2xl font-extrabold text-[#206A4F] dark:text-emerald-400">
                <motion.div variants={itemVariants}>
                  <Link href="/articles" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 hover:translate-x-2 rtl:hover:-translate-x-2 transition-transform">
                    {t.nav.articles}
                  </Link>
                </motion.div>
                <motion.div variants={itemVariants}>
                  <Link href="/volunteer-form" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 hover:translate-x-2 rtl:hover:-translate-x-2 transition-transform">
                    {t.nav.volunteerForm}
                  </Link>
                </motion.div>
                <motion.div variants={itemVariants}>
                  <Link href="/contact-us" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 hover:translate-x-2 rtl:hover:-translate-x-2 transition-transform">
                    {t.nav.contactUs}
                  </Link>
                </motion.div>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.4 }}
                className="mt-12 flex flex-col gap-6"
              >
                <div className="w-full h-px bg-[#206A4F]/10 dark:bg-emerald-400/10 rounded-full" />
                
                <div className="flex items-center justify-between">
                  <AuthUI />
                  
                  <div className="flex items-center gap-3">
                    <I18nSwitcher />
                    <TelegramThemeSwitcher />
                  </div>
                </div>
              </motion.div>

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}